'use client';

import { useNavigate } from 'react-router-dom';
import { useCallback, useState, useEffect, lazy, Suspense } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { type AuthSchema, authSchema } from '@schemas/auth-schema';
import useAuth from '@hooks/use-auth';
import { TextInput, PasswordInput, Button, Modal, Stack, Text, PinInput, Divider, Alert } from '@mantine/core';
import { pageUrl } from '@constants/url';
import { mfaService } from '../../services/mfa-service';
import MFASetupModal from '@components/mfa-setup-modal/MFASetupModal';
import MFAAddDeviceModal from '@components/mfa-add-device-modal/MFAAddDeviceModal';
import { notifications } from '@mantine/notifications';
import type { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import axiosInstance from '@libs/axios';
import { useTheme } from '@contexts/ThemeContext';
import { DARK_THEME_MODES } from '@constants/colors';
import LanguageSwitcher from '@components/language/LanguageSwitcher';
import LoginBackground from '@components/login-background/LoginBackground';
import '@components/login-background/login-form-glass.css';
import { loginGlassModal } from '@components/login-background/loginGlassModal';

// The right-hand feature stage is desktop-only and animation-heavy; keep it out of the login bundle's critical path.
const LoginShowcase = lazy(() => import('@components/login-showcase/LoginShowcase'));
import { useLanguage } from '@contexts/LanguageContext';
import {
  CCN_RETURN_TO_KEY,
  buildCubeSsoPayload,
  getCubeConfig,
  initializeCubeSdk,
  PRODUCT_REDIRECT_DELAY_MS,
  productPageForLoginError,
  triggerCubeLogin,
} from '@/services/cube-sso';
import { clearAutoLoginAttempted } from '@/app/ccn/callback/auto-login';
import {
  CCN_HOME,
  clearJarvisSession,
  completeAuthenticatedSession,
  hydrateSessionFromMasterUser,
  isCcnSession,
} from '@utils/auth-session';
import { DEMO_ENDED_PARAM, DEMO_ENDED_VALUE } from '@utils/demo-account';
import { SESSION_INCONSISTENT_PARAM, SESSION_INCONSISTENT_VALUE } from '@utils/session-reconcile';
import { IMPERSONATION_ENDED_PARAM, IMPERSONATION_ENDED_VALUE } from '@utils/impersonation-ended';

export default function LoginPage() {
  const { isLoading, login, loginWithCubeSso, setIsAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { mode, colors: themeColors } = useTheme();
  // Shell + ink for the MFA dialogs, which sit on the Scanner backdrop.
  const glassModal = loginGlassModal(mode);
  const { t } = useLanguage();
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [mfaSetupData, setMFASetupData] = useState<{
    qrCodeUrl: string;
    secret: string;
    backupCodes: string[];
    deviceId: number;
  } | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [showMFAVerify, setShowMFAVerify] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [cubeError, setCubeError] = useState('');
  // Set by the API client when a demo account's window closes mid-session.
  const [demoEnded] = useState(
    () =>
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get(DEMO_ENDED_PARAM) === DEMO_ENDED_VALUE
  );
  // Set by reconcileSession when the stored session disagreed with the token.
  const [sessionInconsistent] = useState(
    () =>
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get(SESSION_INCONSISTENT_PARAM) === SESSION_INCONSISTENT_VALUE
  );
  // Set by the API client when the backend ended an impersonation session.
  const [switchUserEnded] = useState(
    () =>
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get(IMPERSONATION_ENDED_PARAM) === IMPERSONATION_ENDED_VALUE
  );
  const [isCubeLoading, setIsCubeLoading] = useState(false);

  useEffect(() => {
    void initializeCubeSdk({
      onError: (error: unknown) => {
        console.error('[CUBE SSO] SDK callback error:', error);
      },
    }).catch((error) => {
      console.warn('[CUBE SSO] SDK initialization skipped:', error);
    });
  }, []);

  // Check on mount if user is logged in but MFA not enabled
  useEffect(() => {
    const accessToken = Cookies.get('accessToken') || localStorage.getItem('accessToken');
    const mfaEnabled = localStorage.getItem('mfaEnabled');
    const authData = localStorage.getItem('authData');

    // Check if user was in the middle of MFA verification (session token exists)
    const storedSessionToken = sessionStorage.getItem('mfaSessionToken');
    const storedEmail = sessionStorage.getItem('mfaEmail');

    if (storedSessionToken && storedEmail) {
      setSessionToken(storedSessionToken);
      setUserEmail(storedEmail);
      setShowMFAVerify(true);
      return;
    }

    // Only redirect if user is fully authenticated (has token AND MFA is enabled)
    if (accessToken && mfaEnabled === 'true') {
      const parsedAuthData = authData ? JSON.parse(authData) : null;
      const email = parsedAuthData?.email || '';
      // A CCN-restricted session only has the CCN workspace.
      if (isCcnSession()) {
        navigate(CCN_HOME, { replace: true });
        return;
      }
      // Redirect Samuel to dashboard, others to doc-pabean
      if (email.toLowerCase().includes('samuel')) {
        navigate(pageUrl.dashboard);
      } else {
        navigate(pageUrl.docPabean);
      }
      return;
    }

    // If user has token but MFA is not enabled, they need to complete MFA setup
    // BUT don't automatically trigger setup on refresh - let them click login again
    if (accessToken && mfaEnabled === 'false' && authData) {
      const parsedAuthData = JSON.parse(authData);
      const email = parsedAuthData.email;
      setUserEmail(email);

      // Clear the incomplete auth state
      // User needs to login again to properly set up MFA
      localStorage.removeItem('accessToken');
      localStorage.removeItem('authData');
      localStorage.removeItem('mfaEnabled');
      Cookies.remove('accessToken');
      Cookies.remove('refreshToken');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { handleSubmit, control } = useForm({
    resolver: yupResolver(authSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const redirectAfterLogin = useCallback(
    (email: string) => {
      // CCN-restricted sessions land in the CCN workspace, honouring the
      // deep link the user arrived with (stored by /ccn/open).
      if (isCcnSession()) {
        const returnTo = sessionStorage.getItem(CCN_RETURN_TO_KEY);
        sessionStorage.removeItem(CCN_RETURN_TO_KEY);
        navigate(returnTo?.startsWith(CCN_HOME) ? returnTo : CCN_HOME, { replace: true });
        return;
      }
      // Redirect Samuel to dashboard, others to doc-pabean
      if (email.toLowerCase().includes('samuel')) {
        navigate(pageUrl.dashboard);
      } else {
        navigate(pageUrl.docPabean);
      }
    },
    [navigate]
  );

  const handleLogin: SubmitHandler<AuthSchema> = async (data) => {
    setUserEmail(data.email);
    setCubeError('');

    try {
      // Call login API
      const loginResult = await login(data);

      // Login returned null/undefined = failed inside useAuth
      if (!loginResult) {
        return;
      }

      // Check if MFA verification is required (user has MFA enabled)
      if (loginResult.kind === 'mfa_required') {
        // Store session token in sessionStorage so it persists on refresh
        sessionStorage.setItem('mfaSessionToken', loginResult.sessionToken);
        sessionStorage.setItem('mfaEmail', data.email);
        // Store session token and show MFA verification modal
        setSessionToken(loginResult.sessionToken);
        setShowMFAVerify(true);
        return;
      }

      // If login was successful, check MFA status
      if (loginResult.kind === 'authenticated') {
        // Check if MFA is enabled for this user
        if (loginResult.enabledMfa === false) {
          // User needs to set up MFA - don't redirect yet
          await setupMFA(data.email);
        } else {
          // MFA is enabled and verified, redirect to app
          redirectAfterLogin(data.email);
        }
      }
    } catch (err) {
      console.error('[LOGIN] Unexpected error:', err);
    }
  };

  const handleCubeLogin = async () => {
    setCubeError('');

    // Redirect mode sends the whole tab to Microsoft, which returns to the
    // page that started the sign-in. Only /ccn/callback knows how to finish
    // it; started from here, the exchange below was cut off by the
    // navigation and the user landed back on this page. So hand over.
    if (getCubeConfig().loginType === 'redirect') {
      clearAutoLoginAttempted();
      navigate(`${CCN_HOME}/callback`, { replace: true });
      return;
    }

    setIsCubeLoading(true);

    try {
      await triggerCubeLogin();
      const payload = await buildCubeSsoPayload();
      const result = await loginWithCubeSso(payload);

      if (!result?.success) {
        throw new Error('CUBE SSO exchange failed');
      }

      notifications.show({
        title: 'Login Successful',
        message: 'Signed in with your CUBEforall account',
        color: 'green',
      });

      redirectAfterLogin(payload.email);
    } catch (error) {
      clearJarvisSession();
      setIsAuthenticated(false);
      // No JARVIS subscription: tell the user, then open CCN's product page.
      const product = productPageForLoginError(error);
      if (product) {
        setCubeError(t('ccn.sso.inactiveRedirect'));
        notifications.show({ title: 'CUBEforall', message: t('ccn.sso.inactiveRedirect'), color: 'yellow' });
        window.setTimeout(() => window.location.assign(product), PRODUCT_REDIRECT_DELAY_MS);
        return;
      }
      const message = error instanceof Error ? error.message : 'CUBE SSO login failed';
      setCubeError(message);
      notifications.show({
        title: 'CUBE SSO Error',
        message,
        color: 'red',
      });
    } finally {
      setIsCubeLoading(false);
    }
  };

  const setupMFA = async (email: string) => {
    try {
      // Get full email from auth data or parameter
      const authData = localStorage.getItem('authData');
      let userEmail = email;
      if (authData) {
        const parsed = JSON.parse(authData);
        userEmail = parsed.email || email;
      }

      // Call setup API
      const setupResponse = await mfaService.setupMFA('Smartphone', userEmail);

      // Show MFA setup modal
      setMFASetupData({
        qrCodeUrl: setupResponse.data.qrCodeUrl,
        secret: setupResponse.data.secret,
        backupCodes: setupResponse.data.backupCodes,
        deviceId: setupResponse.data.deviceId,
      });
      setShowMFASetup(true);
    } catch (error: unknown) {
      console.error('Error setting up MFA:', error);

      // Check if it's a specific error that indicates MFA is already being set up
      const axiosError = error as AxiosError<{ error?: { message?: string }; message?: string }>;
      const errorMessage = axiosError.response?.data?.error?.message || axiosError.response?.data?.message;
      const isAlreadySetupError =
        errorMessage?.toLowerCase().includes('already') ||
        errorMessage?.toLowerCase().includes('pending') ||
        axiosError.response?.status === 409;

      if (isAlreadySetupError) {
        // Check if we can get the existing setup from the API
        try {
          const statusResponse = await mfaService.getStatus();
          if (statusResponse.data.mfaEnabled) {
            // MFA is actually already enabled, update state and redirect
            localStorage.setItem('mfaEnabled', 'true');
            setIsAuthenticated(true);
            redirectAfterLogin(email);
            return;
          }
        } catch (statusError) {
          console.error('Failed to check MFA status:', statusError);
        }

        // Show a more user-friendly message
        notifications.show({
          title: 'MFA Setup',
          message: 'Please complete your pending MFA setup or contact support',
          color: 'yellow',
        });
      } else {
        // Show error for other types of failures
        notifications.show({
          title: 'Error',
          message: 'Failed to setup MFA. Please try logging in again.',
          color: 'red',
        });
      }

      // On error, clear auth state to allow fresh login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('authData');
      localStorage.removeItem('mfaEnabled');
      Cookies.remove('accessToken');
      Cookies.remove('refreshToken');
    }
  };

  const _checkMFAStatus = useCallback(
    async (email: string) => {
      try {
        // Verify token exists before calling API
        const cookieToken = Cookies.get('accessToken');
        const localToken = localStorage.getItem('accessToken');

        if (!cookieToken && !localToken) {
          console.error('No token found! Cannot check MFA status');
          redirectAfterLogin(email);
          return;
        }

        // Check MFA status
        try {
          const statusResponse = await mfaService.getStatus();

          if (!statusResponse.data.mfaEnabled) {
            // MFA not enabled, trigger setup
            await setupMFA(email);
          } else {
            // MFA already enabled, redirect to app
            redirectAfterLogin(email);
          }
        } catch (statusError) {
          console.error('Error from MFA status API:', statusError);

          const status = (statusError as AxiosError)?.response?.status;

          // If we get 401 or 404 right after login, it's likely a backend issue
          // Just skip MFA setup and redirect to app
          if (status === 401 || status === 404) {
            // Don't show notification for this - it's expected during development
            redirectAfterLogin(email);
          } else {
            // For other errors, show error and redirect
            notifications.show({
              title: 'Error',
              message: 'Failed to check MFA status. Redirecting to app.',
              color: 'red',
            });
            redirectAfterLogin(email);
          }
        }
      } catch (error) {
        console.error('Unexpected error in checkMFAStatus:', error);
        redirectAfterLogin(email);
      }
    },
    [redirectAfterLogin]
  );

  const handleVerifyMFA = async (code: string) => {
    if (!mfaSetupData) return;

    setIsVerifying(true);
    try {
      // Verify the code
      await mfaService.verifySetup(mfaSetupData.deviceId, code);

      // Check status again to confirm
      const statusResponse = await mfaService.getStatus();

      if (statusResponse.data.mfaEnabled) {
        // Update MFA enabled status in localStorage
        localStorage.setItem('mfaEnabled', 'true');

        // Final re-sync before the app loads. This is the last step of a new
        // user's first login, and the only one that mirrors what
        // /complete-mfa-login does on every later login — without it, a session
        // that started before the PPJK link resolved would keep an empty
        // `nomor_ppjk` until the user logged out and back in.
        await hydrateSessionFromMasterUser();

        // Now mark user as authenticated since MFA setup is complete
        setIsAuthenticated(true);

        notifications.show({
          title: 'Success!',
          message: 'MFA successfully activated',
          color: 'green',
        });

        setShowMFASetup(false);
        // Redirect to app
        redirectAfterLogin(userEmail);
      } else {
        notifications.show({
          title: 'Error',
          message: 'MFA not active yet, please try again',
          color: 'red',
        });
      }
    } catch (error) {
      console.error('Error verifying MFA:', error);
      notifications.show({
        title: 'Error',
        message: 'Verification code is incorrect, please try again',
        color: 'red',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleMFAVerify = async (code: string) => {
    try {
      setIsVerifying(true);

      // Call complete-mfa-login endpoint
      const response = await axiosInstance.post('/api/v1/auth/complete-mfa-login', {
        sessionToken,
        code,
      });

      // If successful, extract and store tokens
      if (response.data.success && response.data.data.accessToken) {
        const { accessToken, refreshToken, email, id, ppjkNpwp } = response.data.data;

        // Clear session storage since MFA is complete
        sessionStorage.removeItem('mfaSessionToken');
        sessionStorage.removeItem('mfaEmail');

        completeAuthenticatedSession({
          accessToken,
          refreshToken,
          authData: { email, id, ppjkNpwp },
        });
        await hydrateSessionFromMasterUser();

        notifications.show({
          title: 'Success!',
          message: 'MFA verification successful',
          color: 'green',
        });

        // Redirect to app
        setShowMFAVerify(false);
        redirectAfterLogin(email || userEmail);
      }
    } catch (error) {
      console.error('[MFA VERIFY] Error caught:', error);

      notifications.show({
        title: 'Error',
        message: 'Invalid verification code. Please try again.',
        color: 'red',
      });

      // Clear the PIN input on error but STAY on modal
      setMfaCode('');

      // DO NOT close modal or redirect - user should try again
    } finally {
      setIsVerifying(false);
    }
  };

  const _handleAddDevice = () => {
    // Open the add device modal
    setShowAddDevice(true);
  };

  const handleDeviceAdded = () => {
    // Show success notification when device is added and verified
    notifications.show({
      title: 'Device Added Successfully',
      message: 'The new device has been added and verified. Your team member can now use it to login.',
      color: 'green',
      autoClose: 5000,
    });
  };

  const handleBackToLogin = () => {
    // Clear session storage
    sessionStorage.removeItem('mfaSessionToken');
    sessionStorage.removeItem('mfaEmail');
    // Clear states and go back to login
    setShowMFAVerify(false);
    setSessionToken('');
    setMfaCode('');
    setUserEmail('');
  };

  return (
    <>
      {/* MFA Add Device Modal */}
      {showAddDevice && (
        <MFAAddDeviceModal
          opened={showAddDevice}
          onClose={() => setShowAddDevice(false)}
          onDeviceAdded={handleDeviceAdded}
          sessionToken={sessionToken}
        />
      )}

      {/* MFA Verification Modal (during login) */}
      {showMFAVerify && (
        <Modal
          opened={showMFAVerify}
          onClose={() => {}}
          withCloseButton={false}
          closeOnClickOutside={false}
          closeOnEscape={false}
          centered
          size="md"
          zIndex={10000}
          overlayProps={glassModal.overlayProps}
          styles={glassModal.styles}
        >
          {/* login-glass so the PIN boxes get the same material as the form. */}
          <div className="login-glass" data-login-surface={glassModal.surface}>
            <Stack gap="xl" py="md">
              {/* Header with Icon */}
              <Stack gap="md" align="center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: glassModal.accentHalo,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8"
                    style={{ color: glassModal.accent }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <title>Two-factor authentication</title>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>

                <Stack gap="xs" align="center">
                  <Text size="xl" fw={700} ta="center" style={{ color: glassModal.text }}>
                    Two-Factor Authentication
                  </Text>
                  <Text size="sm" ta="center" className="max-w-sm" style={{ color: glassModal.textMuted }}>
                    Please enter the 6-digit verification code from your authenticator app
                  </Text>
                </Stack>
              </Stack>

              {/* PIN Input */}
              <Stack gap="md" align="center">
                <PinInput
                  length={6}
                  type="number"
                  size="xl"
                  value={mfaCode}
                  onChange={(value) => {
                    setMfaCode(value);
                    if (value.length === 6) {
                      handleMFAVerify(value);
                    }
                  }}
                  placeholder=""
                  oneTimeCode
                  disabled={isVerifying}
                />

                {isVerifying && (
                  <Text size="sm" ta="center" style={{ color: glassModal.textMuted }}>
                    Verifying code...
                  </Text>
                )}
              </Stack>

              {/* Help Text */}
              <Stack gap="xs" mt="md">
                <Text size="xs" ta="center" style={{ color: glassModal.textMuted }}>
                  Open your authenticator app (e.g., Google Authenticator, Authy) to view your verification code
                </Text>
              </Stack>

              {/* Back Button */}
              <Divider my="lg" />
              <Button variant="subtle" color="gray" fullWidth onClick={handleBackToLogin} disabled={isVerifying}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <title>Back to login</title>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Kembali ke Login
              </Button>
            </Stack>
          </div>
        </Modal>
      )}

      {/* MFA Setup Modal */}
      {showMFASetup && mfaSetupData && (
        <MFASetupModal
          opened={showMFASetup}
          qrCodeUrl={mfaSetupData.qrCodeUrl}
          secret={mfaSetupData.secret}
          backupCodes={mfaSetupData.backupCodes}
          deviceId={mfaSetupData.deviceId}
          onVerify={handleVerifyMFA}
          onClose={() => {}}
          isVerifying={isVerifying}
        />
      )}

      <LoginBackground />

      {/* Above the fixed Scanner backdrop. */}
      <div className="relative z-10 flex min-h-screen">
        {/*
         * absolute, not fixed: on a short phone the page scrolls, and a fixed
         * switcher stayed pinned while the heading slid underneath it. The
         * parent is relative and min-h-screen, so on desktop -- where the page
         * does not scroll -- this lands in exactly the same place.
         */}
        <div className="absolute top-4 right-4 z-50 w-40">
          <LanguageSwitcher position="top-end" />
        </div>

        <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 py-12 sm:px-8 lg:px-8 lg:py-0 xl:px-4">
          {/* login-glass: form controls borrow the showcase panel's material. */}
          <div
            className="login-glass mx-auto w-full max-w-sm"
            data-login-surface={DARK_THEME_MODES.includes(mode) ? 'dark' : 'light'}
          >
            <div>
              <img
                className="h-7 w-auto"
                src={
                  DARK_THEME_MODES.includes(mode)
                    ? '/image/png/jarvis-wordmark-white.png'
                    : '/image/png/jarvis-wordmark.png'
                }
                alt="JARVIS"
              />
              <h2 className="mt-8 text-xl font-semibold lg:mt-6">{t('auth.welcome')}</h2>
            </div>

            <div className="mt-10 lg:mt-8">
              <form className="space-y-6" onSubmit={handleSubmit(handleLogin)}>
                {demoEnded && (
                  <Alert color="blue" radius="md" variant="light" title={t('auth.demoEndedTitle')}>
                    {t('auth.demoEndedMessage')}
                  </Alert>
                )}
                {sessionInconsistent && (
                  <Alert color="yellow" radius="md" variant="light">
                    {t('auth.sessionInconsistent')}
                  </Alert>
                )}
                {switchUserEnded && (
                  <Alert color="blue" radius="md" variant="light">
                    {t('auth.switchUserEnded')}
                  </Alert>
                )}
                {cubeError && (
                  <Alert color="red" radius="md" variant="light" title={t('auth.cubeErrorTitle')}>
                    {cubeError}
                  </Alert>
                )}
                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextInput
                      id="email"
                      radius={12}
                      label={t('auth.email')}
                      placeholder={t('auth.emailPlaceholder')}
                      error={error?.message}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="password"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <PasswordInput
                      id="password"
                      radius={12}
                      label={t('auth.password')}
                      placeholder={t('auth.passwordPlaceholder')}
                      error={error?.message}
                      mt="md"
                      {...field}
                    />
                  )}
                />

                <div className="mt-8">
                  <Button
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    type="submit"
                    loading={isLoading}
                    disabled={isLoading}
                    fullWidth
                    loaderProps={{ type: 'dots' }}
                  >
                    {t('auth.signIn')}
                  </Button>
                </div>

                <Divider label={t('auth.or')} labelPosition="center" my="md" />

                <Button
                  variant="default"
                  type="button"
                  fullWidth
                  loading={isCubeLoading}
                  disabled={isLoading || isCubeLoading}
                  onClick={handleCubeLogin}
                >
                  <span className="flex items-center justify-center gap-3">
                    <img
                      src="/image/png/ccn.png"
                      alt="CUBEforall by CCN"
                      className="h-6 w-6 rounded-sm object-contain"
                    />
                    <span>{t('auth.cubeLogin')}</span>
                  </span>
                </Button>
              </form>
            </div>

            <div className="mt-8 text-center lg:mt-6">
              <button
                type="button"
                className="text-xs hover:underline"
                style={{ color: themeColors.primary }}
                onClick={() => navigate(pageUrl.approval.reviewerLogin)}
              >
                {t('auth.reviewerLogin')}
              </button>
            </div>

            <div className="mt-8 text-center text-xs leading-relaxed text-gray-500 lg:mt-4">
              JARVIS Version 1.0.0 (build 484)
              <br />© 2022 - 2026 JARVIS. All rights reserved.
            </div>
          </div>
        </div>

        {/*
         * No overflow-hidden here: the showcase panel clips its own contents,
         * and this column clipped the panel's drop shadow dead at the midline,
         * which reads as a hard vertical seam against the Scanner backdrop.
         */}
        <div className="hidden lg:flex lg:w-1/2">
          <div className="w-full flex m-9">
            <Suspense fallback={<div className="w-full h-full rounded-3xl bg-[#0B1C2B]/70" aria-hidden="true" />}>
              <LoginShowcase />
            </Suspense>
          </div>
        </div>
      </div>
    </>
  );
}
